import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { IReactionDisposer, reaction } from 'mobx';
import { FlightList, FlightListShimmer } from './list';
import { FlightSearch } from '../../search/ui';
import { FlightsNavigationButton } from './FlightsNavigationButton';
import { FlightsNotFound } from './list/FlightsNotFound';
import { ISelectDay } from '../../search/ui/FlightDateSelect';
import { SolidContentWrap } from '../../../shared/components';
import { FlightListScope } from '../domain/constants/FlightListScope';
import { replaceHash } from '../../../shared/router/hash/replaceHash';
import { IFlightSearchSuggestion } from '../../search/domain/models/IFlightSearchSuggestion';
import {
	trackDateSelectChange,
	trackQueryChange,
	trackResultsChange,
	trackScopeChange,
	trackShowMore,
	trackSuggestionSelect,
} from '../../shared/analytics';
import { FlightListStore } from '../domain/stores';
import { isServerSide } from '../../../shared/utils/isServerSide';
import { IStores } from '../../../app/root/domain/stores/createStores';
import { getCurrentHash } from '../../../shared/router/utils/getCurrentHash';

interface IProps {}

interface IInjectedProps {
	flightListStore: FlightListStore;
}

type Props = IProps & IInjectedProps;

const URL_HASH_ALL = 'all';
const URL_HASH_ARRIVALS = 'arrivals';
const URL_HASH_DEPARTURES = 'departures';

export const FlightsOverview = inject(({ flightListStore }: IStores) => ({
	flightListStore,
}))(
	observer(
		class FlightsOverview extends Component<IProps> {
			private disposeQueryReaction: IReactionDisposer;

			private disposeResultsReaction: IReactionDisposer;

			private disposeLoadingReaction: IReactionDisposer;

			constructor(props: IProps) {
				super(props);

				this.disposeQueryReaction = reaction(
					() => this.injected.flightListStore.query,
					this.onChangeQuery,
				);

				this.disposeResultsReaction = reaction(
					() => this.injected.flightListStore.sortedFlights,
					this.onChangeResults,
				);

				this.disposeLoadingReaction = reaction(
					() => this.injected.flightListStore.isLoading,
					this.onChangeResults,
				);
			}

			protected get injected() {
				return this.props as Props;
			}

			public componentDidMount() {
				this.updateScopeByHash();

				const { selectedDay } = this.injected.flightListStore;
				this.injected.flightListStore.loadDay(selectedDay);

				if (!isServerSide()) {
					// Using a hash interferes with the scroll to element behavior, so we need to manually scroll the page to the top.
					window.scrollTo(0, 0);
				}
			}

			public componentWillUnmount() {
				this.disposeQueryReaction();
				this.disposeResultsReaction();
				this.disposeLoadingReaction();

				this.injected.flightListStore.reset();
			}

			public showLaterFlights = () => {
				this.injected.flightListStore.showLater();

				trackShowMore('later');
			};

			public showEarlierFlights = () => {
				this.injected.flightListStore.showEarlier();

				trackShowMore('earlier');
			};

			public onChangeScope = (scope: FlightListScope) => {
				if (scope === this.injected.flightListStore.scope) {
					return;
				}

				this.updateHashByScope(scope);
				this.injected.flightListStore.setScope(scope);

				trackScopeChange(scope);
			};

			protected onChangeDate = (selected: ISelectDay | null) => {
				const { flightListStore } = this.injected;

				if (!selected) {
					return;
				}

				if (flightListStore.selectedDay.isSame(selected.date, 'day')) {
					return;
				}

				flightListStore.loadDay(selected.date);

				trackDateSelectChange(selected);
			};

			protected onChangeQuery = () => {
				const { query } = this.injected.flightListStore;

				trackQueryChange(query);
			};

			protected onChangeResults = () => {
				const { isLoading } = this.injected.flightListStore;

				if (isLoading) {
					return;
				}

				const { selectedDay, scope, query } = this.injected.flightListStore;

				const { totalResults } = this.injected.flightListStore;

				trackResultsChange(selectedDay, scope, query, totalResults);
			};

			protected onSelectFlightSuggestion = (
				selectedItem: IFlightSearchSuggestion | null,
			) => {
				trackSuggestionSelect(selectedItem);
			};

			protected updateHashByScope(scope: FlightListScope) {
				if (isServerSide()) {
					return;
				}

				switch (scope) {
					case FlightListScope.Arrivals:
						replaceHash(URL_HASH_ARRIVALS);
						break;

					case FlightListScope.Departures:
						replaceHash(URL_HASH_DEPARTURES);
						break;

					default:
						replaceHash(URL_HASH_ALL);
						break;
				}
			}

			protected updateScopeByHash() {
				const hash = getCurrentHash();

				switch (hash) {
					case URL_HASH_ARRIVALS:
						this.injected.flightListStore.setScope(FlightListScope.Arrivals);
						break;

					case URL_HASH_DEPARTURES:
						this.injected.flightListStore.setScope(FlightListScope.Departures);
						break;

					default:
						this.injected.flightListStore.setScope(FlightListScope.All);
						break;
				}
			}

			protected renderFlightList() {
				const { flightListStore } = this.injected;

				if (!flightListStore.finishedInitialLoad || flightListStore.isLoading) {
					return (
						<FlightListShimmer listLength={flightListStore.minimumListLength} />
					);
				}

				if (flightListStore.error) {
					return <FlightsNotFound />;
				}

				const {
					canNavigateForward,
					canNavigateBackward,
					flights,
					loadedFromIndex,
					loadedAmount,
				} = flightListStore;

				return (
					<>
						{flightListStore.flights.length === 0 ? (
							<FlightsNotFound />
						) : (
							<>
								{canNavigateBackward && (
									<FlightsNavigationButton
										onClick={this.showEarlierFlights}
										direction="up"
										absolutePositioning
									/>
								)}

								<FlightList
									flights={flights}
									loadedFromIndex={loadedFromIndex}
									loadedAmount={loadedAmount}
									canNavigateBackward={canNavigateBackward}
								/>

								{canNavigateForward && (
									<FlightsNavigationButton
										onClick={this.showLaterFlights}
										direction="down"
									/>
								)}
							</>
						)}
					</>
				);
			}

			public render() {
				const { scope } = this.injected.flightListStore;

				return (
					<SolidContentWrap>
						<FlightSearch
							onChangeDate={this.onChangeDate}
							onChangeScope={this.onChangeScope}
							onSelectFlightSuggestion={this.onSelectFlightSuggestion}
							currentScope={scope}
						/>
						{this.renderFlightList()}
					</SolidContentWrap>
				);
			}
		},
	),
);
